import React, { useState, useRef, useEffect } from "react";
import { Zap, Target, Shield, Users, Video, X } from "lucide-react";
import { useTeam } from "../../lib/teamConfig";
import { useTheme } from "../../utils/theme";

// Sport-specific prompt configurations
const PROMPT_CONFIG = {
  baseball: {
    pitching: {
      icon: Zap,
      label: "Pitching",
      color: "#E8B800",
      prompts: [
        "Which pitchers have a fastball velocity over 93 MPH?",
        "Show me spin rate analysis for all curveballs",
        "Compare release points across our starting rotation",
        "Find pitchers with high vertical break on sliders",
      ],
    },
    hitting: {
      icon: Target,
      label: "Hitting",
      color: "#22C55E",
      prompts: [
        "Show me hitters with OPS above .850",
        "Find players with highest exit velocity",
        "Compare launch angles across the lineup",
        "Which hitters perform best against lefties?",
      ],
    },
    defense: {
      icon: Shield,
      label: "Defense",
      color: "#3B82F6",
      prompts: [
        "Show me catchers with best pop times",
        "Find middle infielders with highest range factor",
        "Compare outfield arm strength metrics",
        "Which positions need defensive upgrades?",
      ],
    },
    scouting: {
      icon: Users,
      label: "Scouting",
      color: "#A855F7",
      prompts: [
        "Find top transfer portal pitchers in region",
        "Show me 2025 draft-eligible prospects",
        "Compare recruit profiles to current roster",
        "Identify players matching our team needs",
      ],
    },
    video: {
      icon: Video,
      label: "Video",
      color: "#EC4899",
      prompts: [
        "Analyze this pitcher for pitch tipping tells",
        "Break down this hitter's swing mechanics",
        "Find defensive positioning patterns",
        "Identify tendencies in this at-bat video",
      ],
    },
  },
  basketball: {
    shooting: {
      icon: Zap,
      label: "Shooting",
      color: "#FF6B35",
      prompts: [
        "Show me players with best 3-point percentage",
        "Compare shooting efficiency by zone",
        "Find players with consistent free throw form",
        "Analyze shot selection patterns",
      ],
    },
    efficiency: {
      icon: Target,
      label: "Efficiency",
      color: "#22C55E",
      prompts: [
        "Show me players with highest PER",
        "Compare true shooting percentages",
        "Find most efficient scorers per possession",
        "Analyze offensive rating leaders",
      ],
    },
    defense: {
      icon: Shield,
      label: "Defense",
      color: "#3B82F6",
      prompts: [
        "Show me best defensive rating players",
        "Find players with highest steal rates",
        "Compare shot contest effectiveness",
        "Analyze rim protection metrics",
      ],
    },
    recruiting: {
      icon: Users,
      label: "Recruiting",
      color: "#A855F7",
      prompts: [
        "Find transfer portal guards in region",
        "Show me high school prospects matching needs",
        "Compare recruit profiles to roster gaps",
        "Identify players for our system",
      ],
    },
    video: {
      icon: Video,
      label: "Video",
      color: "#EC4899",
      prompts: [
        "Analyze this player's ball handling",
        "Break down this offensive set",
        "Find defensive tendency patterns",
        "Identify pick and roll tendencies",
      ],
    },
  },
  // EuroLeague basketball - Spanish/European context
  euroleague: {
    roster: {
      icon: Users,
      label: "Roster",
      color: "#E8B800",
      prompts: [
        "Who are the top PIR performers on Real Madrid?",
        "Compare Campazzo's playmaking stats",
        "Show Tavares rim protection metrics",
        "Analyze Hezonja's scoring efficiency",
      ],
    },
    efficiency: {
      icon: Target,
      label: "Efficiency",
      color: "#22C55E",
      prompts: [
        "Show PIR leaders on the roster",
        "Compare EuroLeague vs ACB performance",
        "Find players with best assist-to-turnover ratio",
        "Analyze offensive efficiency by position",
      ],
    },
    defense: {
      icon: Shield,
      label: "Defense",
      color: "#3B82F6",
      prompts: [
        "Show defensive rating by player",
        "Compare rim protection stats",
        "Find best perimeter defenders",
        "Analyze pick and roll defense",
      ],
    },
    scouting: {
      icon: Zap,
      label: "Scouting",
      color: "#A855F7",
      prompts: [
        "Analyze Barcelona's offensive system",
        "Compare Punter's scoring tendencies",
        "Find weaknesses in Pascual's defense",
        "Show El Clasico matchup analysis",
      ],
    },
    video: {
      icon: Video,
      label: "Video",
      color: "#EC4899",
      prompts: [
        "Analyze Spain PnR execution",
        "Break down Horns offensive sets",
        "Find defensive switching patterns",
        "Identify transition tendencies",
      ],
    },
  },
};

const PromptIconGrid = ({ onSelectPrompt }) => {
  const { selectedSport } = useTeam();
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [expandedCategory, setExpandedCategory] = useState(null);
  const dropdownRef = useRef(null);

  // Get prompts for current sport - map EURO to euroleague, basketball sports to basketball
  const getSportCategory = (sport) => {
    if (sport === 'EURO') return 'euroleague';
    if (['NBA', 'WNBA', 'MBB', 'WBB', 'GL'].includes(sport)) return 'basketball';
    if (['MLB', 'CBB', 'MiLB'].includes(sport)) return 'baseball';
    return sport;
  };
  const sportCategory = getSportCategory(selectedSport);
  const sportPrompts = PROMPT_CONFIG[sportCategory] || PROMPT_CONFIG.basketball;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExpandedCategory(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleIconClick = (categoryKey) => {
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
  };

  const handlePromptClick = (prompt) => {
    onSelectPrompt(prompt);
    setExpandedCategory(null);
  };

  return (
    <div className="relative flex items-center justify-center gap-3" ref={dropdownRef}>
      {Object.entries(sportPrompts).map(([key, category]) => {
        const Icon = category.icon;
        const isExpanded = expandedCategory === key;

        return (
          <div key={key} className="relative">
            {/* Icon Button - Enhanced Glass Style */}
            <button
              onClick={() => handleIconClick(key)}
              className="group flex items-center justify-center rounded-xl transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                width: 48,
                height: 48,
                background: isExpanded
                  ? `linear-gradient(135deg, ${category.color}25 0%, ${category.color}15 100%)`
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)',
                backdropFilter: 'blur(16px)',
                border: isExpanded
                  ? `1.5px solid ${category.color}50`
                  : '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: isExpanded
                  ? `0 4px 24px ${category.color}25, inset 0 1px 0 rgba(255, 255, 255, 0.1)`
                  : '0 4px 16px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                color: isExpanded
                  ? category.color
                  : 'rgba(255, 255, 255, 0.6)',
                transform: isExpanded ? 'scale(1.05)' : 'scale(1)',
              }}
              title={category.label}
              aria-label={`${category.label} prompts`}
              aria-expanded={isExpanded}
            >
              <Icon size={20} />
            </button>

            {/* Expanded Dropdown - Enhanced Glass Panel */}
            {isExpanded && (
              <div
                className="absolute z-50 mt-3 left-1/2 -translate-x-1/2 min-w-[300px] rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300"
                style={{
                  background: 'linear-gradient(180deg, rgba(13, 13, 18, 0.98) 0%, rgba(10, 10, 14, 0.98) 100%)',
                  backdropFilter: 'blur(24px)',
                  border: `1px solid ${category.color}25`,
                  boxShadow: `0 12px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px ${category.color}15, inset 0 1px 0 rgba(255, 255, 255, 0.05)`,
                }}
              >
                {/* Dropdown Header - Glass Style */}
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{
                    background: `linear-gradient(135deg, ${category.color}10 0%, transparent 100%)`,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${category.color}30 0%, ${category.color}15 100%)`,
                        border: `1px solid ${category.color}40`,
                        boxShadow: `0 4px 12px ${category.color}20`,
                      }}
                    >
                      <Icon size={16} style={{ color: category.color }} />
                    </div>
                    <span
                      className="text-sm font-bold tracking-wide"
                      style={{
                        color: 'white',
                      }}
                    >
                      {category.label}
                    </span>
                  </div>
                  <button
                    onClick={() => setExpandedCategory(null)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                    aria-label="Close"
                  >
                    <X
                      size={12}
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    />
                  </button>
                </div>

                {/* Prompt List - Glass Items */}
                <div className="py-2 px-2">
                  {category.prompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePromptClick(prompt)}
                      className="w-full text-left px-4 py-3.5 text-sm rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] mb-1 last:mb-0"
                      style={{
                        color: '#d1d5db',
                        background: 'transparent',
                        border: '1px solid transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${category.color}12 0%, ${category.color}06 100%)`;
                        e.currentTarget.style.borderColor = `${category.color}30`;
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.boxShadow = `0 2px 12px ${category.color}15`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PromptIconGrid;
